import React, { useState, useRef } from 'react';
import { Search, Plus, HelpCircle, SlidersHorizontal } from 'lucide-react';
import type { QuerySchema, FilterFieldDef, FilterOperator } from './schema';
import { useQueryFilter } from './useQueryFilter';
import { FilterChip } from './FilterChip';
import { FilterPopover } from './FilterPopover';
import { GenericSearchHelpDialog } from './GenericSearchHelpDialog';
import { TextFilterEditor } from './editors/TextFilterEditor';
import { EnumFilterEditor } from './editors/EnumFilterEditor';
import { RangeFilterEditor } from './editors/RangeFilterEditor';
import { DateRangeFilterEditor } from './editors/DateRangeFilterEditor';
import { useI18n } from '@/context/I18nContext';

export interface FilterBarProps {
  initialQuery?: string;
  schema?: QuerySchema<string>;
  onSearch: (query: string) => void;
  placeholder?: string;
  enableFilterUI?: boolean;
  className?: string;
}

export function FilterBar({
  initialQuery = '',
  schema,
  onSearch,
  placeholder,
  enableFilterUI = true,
  className = '',
}: FilterBarProps) {
  const { t } = useI18n();
  const [helpOpen, setHelpOpen] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [activeNewFieldKey, setActiveNewFieldKey] = useState<string | null>(null);

  const addBtnRef = useRef<HTMLButtonElement>(null);
  const newFieldRef = useRef<HTMLDivElement>(null);

  const {
    bareText,
    filters,
    setBareText,
    addFilter,
    updateFilter,
    removeFilter,
    clearAll,
    hasActiveFilters,
  } = useQueryFilter({
    query: initialQuery,
    schema,
    onSearch,
  });

  const [inputVal, setInputVal] = useState(bareText);

  // Sync input value when bareText changes
  React.useEffect(() => {
    setInputVal(bareText);
  }, [bareText]);

  const handleInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setBareText(inputVal.trim());
  };

  const activeKeys = new Set(filters.map((f) => f.key));
  const availableFields = schema
    ? Object.values(schema.fields).filter((f) => !activeKeys.has(f.key))
    : [];

  const activeNewFieldDef: FilterFieldDef | undefined =
    schema && activeNewFieldKey ? schema.fields[activeNewFieldKey] : undefined;

  // ── Mode A: Simple / Legacy text input mode ──────────────────────────────
  if (!enableFilterUI || !schema) {
    return (
      <div className={className}>
        <form onSubmit={handleInputSubmit} className="flex gap-2 items-stretch">
          <input
            type="text"
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            placeholder={placeholder || t('search.placeholder')}
            autoComplete="off"
            spellCheck={false}
            className="flex-1 input-field px-3 py-2 text-sm placeholder:text-text-muted focus:border-border-hover"
          />
          <button
            type="submit"
            className="inline-flex items-center gap-1.5 px-3.5 rounded text-xs font-medium bg-accent text-white hover:bg-accent-hover transition-colors cursor-pointer"
          >
            <Search className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{t('search.button')}</span>
          </button>
        </form>
        {schema && (
          <>
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              className="inline-flex items-center gap-1 mt-1.5 px-1 py-0.5 text-[0.7rem] text-text-muted hover:text-text-secondary transition-colors cursor-pointer rounded"
            >
              <HelpCircle className="w-3 h-3" />
              <span>{t('search.syntaxHelp')}</span>
            </button>
            <GenericSearchHelpDialog
              schema={schema}
              open={helpOpen}
              onClose={() => setHelpOpen(false)}
            />
          </>
        )}
      </div>
    );
  }

  // ── Mode B: Pure Interactive Filter UI ──────────────────────────────────
  return (
    <div className={`space-y-2.5 ${className}`}>
      {/* Top Filter Bar Controls */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Add Filter Button */}
        <div className="relative">
          <button
            ref={addBtnRef}
            type="button"
            onClick={() => setAddMenuOpen((prev) => !prev)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors cursor-pointer shrink-0 ${
              addMenuOpen
                ? 'bg-accent/15 border-accent text-accent'
                : 'bg-bg-surface border-border-default text-text-secondary hover:text-text-primary hover:border-border-hover'
            }`}
          >
            <Plus size={14} />
            <span>{t('filter.add')}</span>
          </button>

          {/* Add Filter Bottom Sheet / Dropdown */}
          <FilterPopover
            open={addMenuOpen}
            onClose={() => setAddMenuOpen(false)}
            title={t('filter.add')}
            triggerRef={addBtnRef}
            align="left"
          >
            {availableFields.length === 0 ? (
              <div className="text-xs text-text-muted py-4 text-center">
                {t('filter.allActive')}
              </div>
            ) : (
              <div className="max-h-[60dvh] sm:max-h-56 overflow-y-auto space-y-1">
                {availableFields.map((f) => {
                  const Icon = f.icon || SlidersHorizontal;
                  return (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => {
                        setActiveNewFieldKey(f.key);
                        setAddMenuOpen(false);
                      }}
                      className="w-full flex items-center justify-between p-3 sm:p-2 rounded-lg text-left text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-3 truncate">
                        <Icon size={16} className="text-accent shrink-0" />
                        <div>
                          <div className="font-medium text-text-primary text-sm sm:text-xs">{t(f.label as any)}</div>
                          {f.description && (
                            <div className="text-[0.7rem] text-text-muted truncate max-w-[220px]">
                              {t(f.description as any)}
                            </div>
                          )}
                        </div>
                      </div>
                      {f.unit && (
                        <span className="text-xs text-text-muted font-mono shrink-0 ml-2">
                          {t(f.unit as any)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </FilterPopover>
        </div>

        {/* Active Filter Chips */}
        {filters.map((filter) => {
          const fieldDef = schema.fields[filter.key];
          if (!fieldDef) return null;
          return (
            <FilterChip
              key={filter.id}
              fieldDef={fieldDef}
              filter={filter}
              onUpdate={(updates) => updateFilter(filter.id, updates)}
              onRemove={() => removeFilter(filter.id)}
            />
          );
        })}

        {/* Clear All Button */}
        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearAll}
            className="text-xs text-text-muted hover:text-red transition-colors px-2 py-1 rounded-md cursor-pointer ml-auto sm:ml-0"
          >
            {t('filter.clearAll')}
          </button>
        )}
      </div>

      {/* Popover editor when selecting a new field from Add Filter menu */}
      {activeNewFieldDef && (
        <div ref={newFieldRef} className="relative">
          <FilterPopover
            open={!!activeNewFieldKey}
            onClose={() => setActiveNewFieldKey(null)}
            title={`${t('filter.add')}: ${t(activeNewFieldDef.label as any)}`}
            triggerRef={newFieldRef}
          >
            {activeNewFieldDef.type === 'enum' ? (
              <EnumFilterEditor
                fieldDef={activeNewFieldDef}
                onApply={(vals) => {
                  if (vals.length === 1) {
                    addFilter(activeNewFieldDef.key, vals[0], '=');
                  } else if (vals.length > 1) {
                    addFilter(activeNewFieldDef.key, vals.join(', '), '=', vals);
                  }
                  setActiveNewFieldKey(null);
                }}
                onClose={() => setActiveNewFieldKey(null)}
              />
            ) : activeNewFieldDef.type === 'range' || activeNewFieldDef.type === 'number' ? (
              <RangeFilterEditor
                fieldDef={activeNewFieldDef}
                onApply={(val, op: FilterOperator, highVal) => {
                  addFilter(activeNewFieldDef.key, val, op, undefined, highVal);
                  setActiveNewFieldKey(null);
                }}
                onClose={() => setActiveNewFieldKey(null)}
              />
            ) : activeNewFieldDef.type === 'date' || activeNewFieldDef.type === 'dateRange' ? (
              <DateRangeFilterEditor
                fieldDef={activeNewFieldDef}
                onApply={(fromVal, toVal) => {
                  addFilter(activeNewFieldDef.key, fromVal, '=', undefined, toVal);
                  setActiveNewFieldKey(null);
                }}
                onClose={() => setActiveNewFieldKey(null)}
              />
            ) : (
              <TextFilterEditor
                fieldDef={activeNewFieldDef}
                onApply={(val) => {
                  addFilter(activeNewFieldDef.key, val, '=');
                  setActiveNewFieldKey(null);
                }}
                onClose={() => setActiveNewFieldKey(null)}
              />
            )}
          </FilterPopover>
        </div>
      )}
    </div>
  );
}
